import { Users, UserCheck, BookOpen, Clock } from "lucide-react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { DashboardStatCard } from "@/features/dashboard/components/DashboardStatCard";
import { DashboardHero } from "@/features/dashboard/components/DashboardHero";
import { ContentStatusWidget } from "@/features/dashboard/components/ContentStatusWidget";
import { RecentLoginsWidget } from "@/features/dashboard/components/RecentLoginsWidget";
import { RecentActivityWidget } from "@/features/dashboard/components/RecentActivityWidget";
import { SystemHealthWidget } from "@/features/dashboard/components/SystemHealthWidget";
import { QuickActionsWidget } from "@/features/dashboard/components/QuickActionsWidget";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useDashboardData } from "@/features/dashboard/hooks/useDashboardData";
import { useUser } from "@/features/auth/store";
import { getRoleDisplayName } from "@/lib/auth-utils";
import { Pages } from "@/constants/enums";
import { appPath } from "@/constants/paths";

export default function DashboardPage() {
  const user = useUser();
  // 🔴 EACH SECTION IS GATED ON THE PERMISSION ITS DATA NEEDS, NOT ON view-dashboard. These used
  // to be gated on view-dashboard, which is UI-scope and unlocks no endpoint — so the sections
  // rendered for anyone who could open the page, while the queries behind them 403'd. The flags
  // come back from useDashboardData so the gate and the `enabled` on the query are the same value.
  const {
    usersData,
    contentData,
    recentLogins,
    recentAudit,
    systemHealth,
    isLoading,
    isAnalyticsLoading,
    isActivityLoading,
    isHealthLoading,
    error,
    canViewAnalytics,
    canViewActivity,
  } = useDashboardData();

  // A stat card links to its list only when the viewer may open it — otherwise the click lands on
  // "وصول مرفوض". The number itself still shows: it comes from the analytics permission.
  const { has } = useUserPermissions();
  const usersLink = has("users:read") ? appPath(Pages.USERS) : undefined;
  const pagesLink = has("content:edit") ? appPath("pages") : undefined;

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email ||
    "";
  const roleName = user?.role ? getRoleDisplayName(user.role) : "";

  return (
    <AdminPageShell error={error}>
      <div className="space-y-8">
        <DashboardHero displayName={displayName} roleName={roleName} />

        {canViewAnalytics && (
          <section className="space-y-4">
            <SectionHeader title="مؤشرات عامة" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <DashboardStatCard
                title="إجمالي المستخدمين"
                value={usersData?.total}
                subtitle="جميع الحسابات المسجّلة"
                icon={Users}
                accent="primary"
                isLoading={isLoading}
                to={usersLink}
              />
              <DashboardStatCard
                title="المستخدمون النشطون"
                value={usersData?.active}
                subtitle="حسابات مفعّلة"
                icon={UserCheck}
                accent="accent"
                isLoading={isLoading}
                to={usersLink}
              />
              <DashboardStatCard
                title="المحتوى المنشور"
                value={contentData?.totalPublished}
                subtitle="صفحات وأخبار ومقالات"
                icon={BookOpen}
                accent="secondary"
                isLoading={isLoading}
                to={pagesLink}
              />
              <DashboardStatCard
                title="قيد المراجعة"
                value={contentData?.totalUnpublished}
                subtitle="في انتظار النشر أو الموافقة"
                icon={Clock}
                accent="muted"
                isLoading={isLoading}
                to={pagesLink}
              />
            </div>
          </section>
        )}

        <section className="space-y-4">
          <SectionHeader title="العمليات والأنظمة" />
          <div
            className={`grid grid-cols-1 gap-6 ${user?.role === "SUPER_ADMIN" ? "lg:grid-cols-2" : ""}`}
          >
            <QuickActionsWidget />
            {/* Platform infrastructure status is the owner's, not a department's. */}
            {user?.role === "SUPER_ADMIN" && (
              <SystemHealthWidget
                data={systemHealth}
                isLoading={isHealthLoading}
              />
            )}
          </div>
        </section>

        {canViewAnalytics && (
          <section className="space-y-4">
            <SectionHeader title="الإشراف والمتابعة" />
            {/*
              One widget now, so the row is single-column: the submissions card that used to sit
              beside this one is gone, and a lone half-width card in a 2-column grid reads as a
              card that failed to load.
            */}
            <div className="grid grid-cols-1 gap-6">
              <ContentStatusWidget
                data={contentData}
                isLoading={isAnalyticsLoading}
              />
            </div>
          </section>
        )}

        {(canViewAnalytics || canViewActivity) && (
          <section className="space-y-4">
            <SectionHeader title="النشاط والمراقبة" />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {canViewAnalytics && (
                <RecentLoginsWidget
                  data={recentLogins}
                  isLoading={isAnalyticsLoading}
                />
              )}
              {canViewActivity && (
                <RecentActivityWidget
                  data={recentAudit}
                  isLoading={isActivityLoading}
                />
              )}
            </div>
          </section>
        )}
      </div>
    </AdminPageShell>
  );
}
